<template>
    <chart-table-wrapper
        v-if="chartOptions"
        :title="props.title"
        :suffix-unit="props.suffix"
        :x-axis-category="props.xAxisCategory"
        :chart-options="chartOptions"
        :chart-categories="categories"
        :additional-csv-export="additionalCsvExport"
    ><template #header>
        <slot name="customHeader" />
    </template>
    </chart-table-wrapper>
</template>

<script lang="ts" setup>
import { Options, SeriesBarOptions } from 'highcharts';
import {
    ref, watch, computed,
} from 'vue';
import { IFilters } from '@/interfaces/storageTracking/IFilters.ts';
import { findCategoryColourCode } from '@/enums/storageTracking/Colours.ts';
import { IBaseHorizontalBarChartData } from '@/interfaces/storageTracking/IBaseHorizontalBarChartData.ts';
import { getTooltipWrapper, getTooltipTableRow } from '@/helpers/charts/chartSettings.ts';
import ChartTableWrapper from '@/components/chart/ChartTableWrapper.vue';
import { getAdditionalCsvExport } from '@/utils/storageTracking/ChartCSV.ts';

const props = defineProps<{
    rawData: Array<{ key: string; value: number }>;
    suffix?: string;
    xAxisCategory?: string;
    filters?: IFilters;
    barColor?: string;
    horizontalAxisTitle?: string;
    title: string;
    excludeLegend?: boolean;
}>();

const chartOptions = ref<Options>();

const categories = computed(() => props.rawData?.map(data => data.key) || []);
const additionalCsvExport = computed<Array<Array<string | number | undefined>>>(() => getAdditionalCsvExport(props.title, props.filters, props.suffix));

function createChartData (): Array<SeriesBarOptions> {
    let allData: IBaseHorizontalBarChartData[] = [];
    if (props.rawData) {
        allData = props.rawData.map((item) => {
            const colourCode = findCategoryColourCode(item.key);
            return { y: item.value, name: item.key, color: (colourCode || props.barColor || '') };
        });
    }

    return [{
        type: 'bar',
        name: props.horizontalAxisTitle?.includes('(') ? props.horizontalAxisTitle.split('(')[0] : props.title,
        data: allData,
    }];
}

function updateChart (): void {
    const yAxisCategories: string[] = props.rawData?.map(data => data.key);

    const customOptions: Options = {
        chart: {
            backgroundColor: 'transparent',
            spacingLeft: 0,
            spacingRight: 0,
        },
        title: {
            text: '',
        },
        yAxis: {
            title: {
                text: props.horizontalAxisTitle,
            },
        },
        xAxis: {
            categories: yAxisCategories,
            labels: {
                useHTML: true,
                allowOverlap: false,
                style: {
                    wordBreak: 'break-all',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    width: 100,
                },
            },
        },
        legend: {
            enabled: !props.excludeLegend,
        },
        tooltip: {
            formatter (this: Highcharts.TooltipFormatterContextObject): string {
                return getTooltipWrapper({
                    content: getTooltipTableRow({
                        label: this.point.category,
                        value: `${this.point.y ? Math.trunc(this.point.y).toLocaleString() : ''} ${props.suffix ?? ''}`,
                    }),
                    contentType: 'table',
                    header: this.point.series.name,
                });
            },
        },
        series: createChartData(),
    };

    chartOptions.value = customOptions;
}

watch(() => props, () => {
    updateChart();
}, { deep: true, immediate: true });

</script>
